import type { MenuPdfItem } from "@lemmonhq/types";

type DeleteAsset = (assetId: string) => Promise<unknown>;

const getAssetIds = (items: MenuPdfItem[]): Set<string> =>
  new Set(items.flatMap((item) => (item.assetId ? [item.assetId] : [])));

export const createMenuPdfAssetLifecycle = (deleteAsset: DeleteAsset) => {
  const pendingUploads = new Set<string>();
  const pendingDeletions = new Set<string>();

  const deleteAssets = async (assetIds: Iterable<string>) => {
    await Promise.all(
      [...new Set(assetIds)].map((assetId) => deleteAsset(assetId)),
    );
  };

  const cleanupPendingUploads = async () => {
    const assetIds = [...pendingUploads];
    if (assetIds.length === 0) return;

    await deleteAssets(assetIds);
    assetIds.forEach((assetId) => pendingUploads.delete(assetId));
  };

  return {
    registerUpload(assetId: string) {
      pendingUploads.add(assetId);
    },

    trackUpdate(currentItems: MenuPdfItem[], nextItems: MenuPdfItem[]) {
      const nextAssetIds = getAssetIds(nextItems);
      currentItems.forEach((item) => {
        if (
          item.assetId &&
          !nextAssetIds.has(item.assetId) &&
          !pendingUploads.has(item.assetId)
        ) {
          pendingDeletions.add(item.assetId);
        }
      });
    },

    async completeSave(savedItems: MenuPdfItem[]) {
      const savedAssetIds = getAssetIds(savedItems);
      // Settings are already persisted. Protect published assets before any
      // fallible cleanup so reset/navigation cannot delete them after a failure.
      savedAssetIds.forEach((assetId) => {
        pendingUploads.delete(assetId);
        pendingDeletions.delete(assetId);
      });
      const orphanedUploads = [...pendingUploads].filter(
        (assetId) => !savedAssetIds.has(assetId),
      );

      await deleteAssets([...pendingDeletions, ...orphanedUploads]);
      pendingUploads.clear();
      pendingDeletions.clear();
    },

    async reset() {
      await cleanupPendingUploads();
      pendingDeletions.clear();
    },

    cleanupPendingUploads,
  };
};
